import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, subDays, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Trash2, X, Pencil, Check } from 'lucide-react';
import { useAuth, useIsParentOrAdmin } from '@/hooks/useAuth';
import api from '@/lib/api';
import { DAY_NAMES } from '@/utils/constants';
import type { Schedule, ScheduleItem, ScheduleAssignment } from '@/types';

const CATEGORY_CONFIG: Record<string, { emoji: string; label: string; color: string }> = {
  meal: { emoji: '🍽️', label: 'Meal', color: '#f97316' },
  diaper: { emoji: '👶', label: 'Diaper', color: '#3b82f6' },
  medication: { emoji: '💊', label: 'Medication', color: '#8b5cf6' },
  exercise: { emoji: '🏃', label: 'Exercise', color: '#10b981' },
  sleep: { emoji: '😴', label: 'Sleep', color: '#6366f1' },
  general: { emoji: '📋', label: 'General', color: '#6b7280' },
};

function formatTime12h(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

export function KaiScheduleTab() {
  const { user } = useAuth();
  const isParentOrAdmin = useIsParentOrAdmin();
  const queryClient = useQueryClient();

  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [manageOpen, setManageOpen] = useState(false);
  const [expandedSchedule, setExpandedSchedule] = useState<string | null>(null);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New item form state
  const [addingItemTo, setAddingItemTo] = useState<string | null>(null);
  const [newItemTime, setNewItemTime] = useState('08:00');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('general');

  // Edit item state
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editItemTime, setEditItemTime] = useState('');
  const [editItemTitle, setEditItemTitle] = useState('');
  const [editItemDescription, setEditItemDescription] = useState('');
  const [editItemCategory, setEditItemCategory] = useState('');

  // Rename schedule state
  const [renamingSchedule, setRenamingSchedule] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');
  const dateDisplay = format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMM d');

  // Fetch resolved schedule for selected date
  const { data: dailyData, isLoading: loadingDaily } = useQuery({
    queryKey: ['schedule-for-date', selectedDate],
    queryFn: async () => {
      const res = await api.get(`/schedules/for-date/${selectedDate}`);
      return res.data as { schedule: { id: string; name: string; assignment_type: string } | null; items: ScheduleItem[] };
    },
    staleTime: 1000 * 10,
  });

  // Fetch all schedules (for manage section)
  const { data: allSchedules = [], isLoading: loadingSchedules } = useQuery({
    queryKey: ['schedules'],
    queryFn: async () => {
      const res = await api.get('/schedules');
      return (res.data.schedules || []) as Schedule[];
    },
    staleTime: 1000 * 10,
    enabled: isParentOrAdmin,
  });

  // Fetch items for expanded schedule
  const { data: expandedItems = [] } = useQuery({
    queryKey: ['schedule-items', expandedSchedule],
    queryFn: async () => {
      const res = await api.get(`/schedules/${expandedSchedule}/items`);
      return (res.data.items || []) as ScheduleItem[];
    },
    staleTime: 1000 * 5,
    enabled: !!expandedSchedule,
  });

  const handlePrevDay = () => setSelectedDate(format(subDays(new Date(selectedDate + 'T12:00:00'), 1), 'yyyy-MM-dd'));
  const handleNextDay = () => setSelectedDate(format(addDays(new Date(selectedDate + 'T12:00:00'), 1), 'yyyy-MM-dd'));
  const handleToday = () => setSelectedDate(format(new Date(), 'yyyy-MM-dd'));

  // Schedule CRUD
  const handleCreateSchedule = async () => {
    if (!newScheduleName.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/schedules', { name: newScheduleName.trim() });
      setNewScheduleName('');
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRenameSchedule = async (id: string) => {
    if (!renameValue.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.patch(`/schedules/${id}`, { name: renameValue.trim() });
      setRenamingSchedule(null);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.delete(`/schedules/${id}`);
      if (expandedSchedule === id) setExpandedSchedule(null);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Items CRUD
  const handleAddItem = async (scheduleId: string) => {
    if (!newItemTitle.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/schedules/${scheduleId}/items`, {
        time: newItemTime,
        title: newItemTitle.trim(),
        description: newItemDescription.trim() || undefined,
        category: newItemCategory,
      });
      setAddingItemTo(null);
      setNewItemTitle('');
      setNewItemDescription('');
      setNewItemCategory('general');
      setNewItemTime('08:00');
      queryClient.invalidateQueries({ queryKey: ['schedule-items', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateItem = async (itemId: string, scheduleId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.patch(`/schedules/items/${itemId}`, {
        time: editItemTime,
        title: editItemTitle.trim(),
        description: editItemDescription.trim() || null,
        category: editItemCategory,
      });
      setEditingItem(null);
      queryClient.invalidateQueries({ queryKey: ['schedule-items', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId: string, scheduleId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.delete(`/schedules/items/${itemId}`);
      queryClient.invalidateQueries({ queryKey: ['schedule-items', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assignments
  const handleAssign = async (scheduleId: string, dayOfWeek: number) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/schedules/${scheduleId}/assign`, { day_of_week: dayOfWeek });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignDate = async (scheduleId: string, date: string) => {
    if (!date) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/schedules/${scheduleId}/assign`, { date });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.delete(`/schedules/assignments/${assignmentId}`);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-for-date'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper: find which schedule is assigned to a day_of_week
  const getAssignmentForDow = (dow: number): { scheduleId: string; assignmentId: string } | null => {
    for (const s of allSchedules) {
      const a = s.assignments.find((a) => a.day_of_week === dow && !a.date);
      if (a) return { scheduleId: s.id, assignmentId: a.id };
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-1">
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h2 className="text-lg font-semibold text-white">Kai's Schedule</h2>
        </div>
        <p className="text-sm text-gray-400">Daily care schedules with timed activities.</p>
      </div>

      {/* Date picker */}
      <div className="flex items-center justify-between bg-card rounded-xl border border-slate-700 p-3">
        <button onClick={handlePrevDay} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <span className="text-white font-medium">{dateDisplay}</span>
          {isToday && <span className="ml-2 text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded">Today</span>}
        </div>
        <div className="flex items-center gap-1">
          {!isToday && (
            <button onClick={handleToday} className="px-2 py-1.5 text-xs bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors">
              Today
            </button>
          )}
          <button onClick={handleNextDay} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Daily view */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        {loadingDaily ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : dailyData?.schedule ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-white font-medium">{dailyData.schedule.name}</h3>
              {dailyData.schedule.assignment_type === 'date' && (
                <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Override</span>
              )}
            </div>
            {dailyData.items.length === 0 ? (
              <p className="text-gray-500 text-sm">No activities in this schedule yet.</p>
            ) : (
              <div className="space-y-2">
                {dailyData.items.map((item) => {
                  const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general;
                  return (
                    <div key={item.id} className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                      <div className="text-sm font-mono text-gray-400 w-20 flex-shrink-0 pt-0.5">
                        {formatTime12h(item.time)}
                      </div>
                      <span className="text-lg flex-shrink-0">{cat.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-gray-400 text-xs mt-0.5">{item.description}</div>
                        )}
                      </div>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                      >
                        {cat.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No schedule set for this day.</p>
          </div>
        )}
      </div>

      {/* Manage section (parent only) */}
      {isParentOrAdmin && (
        <div className="bg-card rounded-xl border border-slate-700">
          <button
            onClick={() => setManageOpen(!manageOpen)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <h3 className="text-white font-medium">Manage Schedules</h3>
            {manageOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
          </button>

          {manageOpen && (
            <div className="px-4 pb-4 space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>
              )}

              {/* Create new schedule */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newScheduleName}
                  onChange={(e) => setNewScheduleName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateSchedule()}
                  placeholder="New schedule name..."
                  className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleCreateSchedule}
                  disabled={isSubmitting || !newScheduleName.trim()}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Create
                </button>
              </div>

              {/* Schedule list */}
              {loadingSchedules ? (
                <div className="text-center py-4 text-gray-400">Loading...</div>
              ) : allSchedules.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No schedules yet. Create one above.</p>
              ) : (
                <div className="space-y-3">
                  {allSchedules.map((schedule) => {
                    const isExpanded = expandedSchedule === schedule.id;
                    const dowAssignments = schedule.assignments.filter((a) => a.day_of_week !== null && !a.date);
                    const dateAssignments = schedule.assignments.filter((a) => a.date);

                    return (
                      <div key={schedule.id} className="bg-slate-800 rounded-lg border border-slate-700">
                        {/* Schedule header */}
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {renamingSchedule === schedule.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSchedule(schedule.id)}
                                  className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleRenameSchedule(schedule.id)} disabled={isSubmitting} className="text-emerald-400 hover:text-emerald-300">
                                  <Check size={16} />
                                </button>
                                <button onClick={() => setRenamingSchedule(null)} className="text-gray-400 hover:text-white">
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => setExpandedSchedule(isExpanded ? null : schedule.id)}
                                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                                >
                                  <span className="text-white font-medium truncate">{schedule.name}</span>
                                  <span className="text-gray-500 text-xs flex-shrink-0">{schedule.item_count} items</span>
                                </button>
                                <button
                                  onClick={() => { setRenamingSchedule(schedule.id); setRenameValue(schedule.name); }}
                                  className="p-1 text-gray-400 hover:text-white rounded transition-colors flex-shrink-0"
                                >
                                  <Pencil size={14} />
                                </button>
                              </>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            disabled={isSubmitting}
                            className="p-1 text-red-400 hover:text-red-300 rounded transition-colors flex-shrink-0 ml-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        {/* Assigned days summary */}
                        {dowAssignments.length > 0 && (
                          <div className="px-3 pb-2 flex flex-wrap gap-1">
                            {dowAssignments.map((a) => (
                              <span key={a.id} className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                                {DAY_NAMES[a.day_of_week!].slice(0, 3)}
                                <button onClick={() => handleRemoveAssignment(a.id)} className="hover:text-white">
                                  <X size={10} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {dateAssignments.length > 0 && (
                          <div className="px-3 pb-2 flex flex-wrap gap-1">
                            {dateAssignments.map((a) => (
                              <span key={a.id} className="inline-flex items-center gap-1 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
                                {a.date}
                                <button onClick={() => handleRemoveAssignment(a.id)} className="hover:text-white">
                                  <X size={10} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="border-t border-slate-700 p-3 space-y-3">
                            {/* Assign to days of week */}
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Assign to day of week</label>
                              <div className="flex flex-wrap gap-1">
                                {DAY_NAMES.map((name, dow) => {
                                  const current = getAssignmentForDow(dow);
                                  const isAssigned = current?.scheduleId === schedule.id;
                                  return (
                                    <button
                                      key={dow}
                                      onClick={() => handleAssign(schedule.id, dow)}
                                      disabled={isSubmitting}
                                      className={`px-2 py-1 text-xs rounded transition-colors ${
                                        isAssigned
                                          ? 'bg-emerald-500 text-white'
                                          : 'bg-slate-700 text-gray-400 hover:bg-slate-600 hover:text-white'
                                      }`}
                                    >
                                      {name.slice(0, 3)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Assign to specific date */}
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Override specific date</label>
                              <input
                                type="date"
                                onChange={(e) => handleAssignDate(schedule.id, e.target.value)}
                                className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            {/* Items list */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-xs text-gray-400">Activities</label>
                                <button
                                  onClick={() => { setAddingItemTo(addingItemTo === schedule.id ? null : schedule.id); }}
                                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                >
                                  <Plus size={14} /> Add
                                </button>
                              </div>

                              {expandedItems.length === 0 && addingItemTo !== schedule.id && (
                                <p className="text-gray-500 text-xs">No activities yet.</p>
                              )}

                              <div className="space-y-1">
                                {expandedItems.map((item) => {
                                  const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general;
                                  if (editingItem === item.id) {
                                    return (
                                      <div key={item.id} className="p-2 bg-slate-700/50 rounded-lg space-y-2">
                                        <div className="flex gap-2">
                                          <input type="time" value={editItemTime} onChange={(e) => setEditItemTime(e.target.value)} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm w-28 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                          <input type="text" value={editItemTitle} onChange={(e) => setEditItemTitle(e.target.value)} className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </div>
                                        <div className="flex gap-2">
                                          <input type="text" value={editItemDescription} onChange={(e) => setEditItemDescription(e.target.value)} placeholder="Description (optional)" className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                          <select value={editItemCategory} onChange={(e) => setEditItemCategory(e.target.value)} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                                              <option key={key} value={key}>{val.emoji} {val.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                          <button onClick={() => setEditingItem(null)} className="px-3 py-1 text-xs bg-slate-600 text-white rounded transition-colors">Cancel</button>
                                          <button onClick={() => handleUpdateItem(item.id, schedule.id)} disabled={isSubmitting} className="px-3 py-1 text-xs bg-emerald-500 text-white rounded transition-colors disabled:opacity-50">Save</button>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-700/30 rounded-lg group">
                                      <span className="text-xs font-mono text-gray-400 w-16">{formatTime12h(item.time)}</span>
                                      <span className="flex-shrink-0">{cat.emoji}</span>
                                      <span className="text-white text-sm flex-1 truncate">{item.title}</span>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={() => {
                                            setEditingItem(item.id);
                                            setEditItemTime(item.time);
                                            setEditItemTitle(item.title);
                                            setEditItemDescription(item.description || '');
                                            setEditItemCategory(item.category);
                                          }}
                                          className="p-1 text-gray-400 hover:text-white rounded"
                                        >
                                          <Pencil size={12} />
                                        </button>
                                        <button onClick={() => handleDeleteItem(item.id, schedule.id)} disabled={isSubmitting} className="p-1 text-red-400 hover:text-red-300 rounded">
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Add item form */}
                              {addingItemTo === schedule.id && (
                                <div className="mt-2 p-3 bg-slate-700/50 rounded-lg space-y-2">
                                  <div className="flex gap-2">
                                    <input type="time" value={newItemTime} onChange={(e) => setNewItemTime(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm w-28 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                    <input type="text" value={newItemTitle} onChange={(e) => setNewItemTitle(e.target.value)} placeholder="Activity title" className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                  </div>
                                  <div className="flex gap-2">
                                    <input type="text" value={newItemDescription} onChange={(e) => setNewItemDescription(e.target.value)} placeholder="Description (optional)" className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                    <select value={newItemCategory} onChange={(e) => setNewItemCategory(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                      {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                                        <option key={key} value={key}>{val.emoji} {val.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="flex gap-2 justify-end">
                                    <button onClick={() => setAddingItemTo(null)} className="px-3 py-1.5 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors">Cancel</button>
                                    <button onClick={() => handleAddItem(schedule.id)} disabled={isSubmitting || !newItemTitle.trim()} className="px-3 py-1.5 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50">Add</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
